import { Icon, IconSet } from '@postscript/components';
import { getVideoMimeType } from 'components/flowBuilder/utils/files';
import { useRef, useState } from 'react';
import styled from 'styled-components';

const StyledVideoWrapper = styled.div`
  position: relative;
`;

const StyledVideo = styled.video<{ $maxHeight?: string }>`
  max-height: ${({ $maxHeight }) => $maxHeight || 'none'};
  max-width: 100%;
  display: block;
`;

const PlayButtonOverlay = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: var(--surface-bkg-color);
  border: none;
  border-radius: var(--border-radius-small);
  display: inline-flex;
  padding: var(--spacing-1) var(--spacing-3);
  align-items: center;
  z-index: 1;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--focus-ring-color, currentColor);
    outline-offset: 2px;
  }
`;

interface VideoPlayerProps {
  url: string;
  maxHeight?: string;
  poster?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onLoadedMetadata?: (event: React.SyntheticEvent<HTMLVideoElement>) => void;
}

const VideoPlayer = ({
  url,
  maxHeight,
  poster,
  onPlay,
  onPause,
  onEnded,
  onLoadedMetadata,
}: VideoPlayerProps): JSX.Element => {
  const [hasStarted, setHasStarted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setHasStarted(true);
    onPlay?.();
  };

  const handlePause = () => {
    onPause?.();
  };

  const handleEnded = () => {
    onEnded?.();
  };

  const handleClick = () => {
    if (videoRef.current && !hasStarted) {
      videoRef.current.play().catch(() => {
        // Silently ignore autoplay rejections (e.g., browser autoplay policy)
      });
    }
  };

  const mimeType = getVideoMimeType(url);

  return (
    <StyledVideoWrapper>
      <StyledVideo
        ref={videoRef}
        $maxHeight={maxHeight}
        controls={hasStarted}
        playsInline
        poster={poster}
        preload={poster ? 'none' : 'auto'}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
        onLoadedMetadata={onLoadedMetadata}
      >
        <source src={url} {...(mimeType && { type: mimeType })} />
      </StyledVideo>
      {!hasStarted && (
        <PlayButtonOverlay
          type="button"
          onClick={handleClick}
          aria-label="Play video"
        >
          <Icon component={IconSet.Play} />
        </PlayButtonOverlay>
      )}
    </StyledVideoWrapper>
  );
};

export default VideoPlayer;
